import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { ComponentDefinition, IValueViewModel, ReconciliationResult, Group, IdentifierType, RefreshClickData, ReconciliationResults } from '../../models';
import { IGroupService, IIdentifierVmService, IRequestService, IDateRangeService } from '../../services';
import { errorTextColor, infoTextColor, warningTextColor } from '../../common/constants';
import { groupScriptUpdated, refreshClick } from '../../common/events';
import { ReconciliationMeasureWidget } from './reconciliationMeasure.widget';
import { Kpi } from '../../wtFramework';
import { getRandomFloat } from '../../common/helpers/getRandom';

class ReconciliationMeasureController implements angular.IComponentController {
    group: Group;
    isLoading: boolean;
    item: ReconciliationMeasureWidget;
    kpiTuples: any[];
    kpiVmList: any[];
    loadingMessage = 'Updating display';
    results: ReconciliationResult[];

    readonly ratio: number;

    private _viewModels: any[];

    static $inject = [
        '$scope',
        'groupService',
        'identifierVmService',
        'requestService',
        'dateRangeService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _groupService: IGroupService,
        private _identifierVmService: IIdentifierVmService,
        private _requestService: IRequestService,
        private _dateRangeService: IDateRangeService) {
    }

    $onInit() {
        this.kpiTuples = [];
        this.init();

        this._$scope.$watch(() => this.results, () => this.refresh());

        this._$scope.$on(groupScriptUpdated, (event) => {
            this.refreshGroup();
        });

        if (this.item) {
            this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
                this.loadReconciliationResults(data.endDate);
            });
            this._$scope.$watch(() => this.item.configuration.groupGuid, (newValue) => {
                if (newValue) {
                    this._groupService.getByGuid(this.item.configuration.groupGuid).then(groupDto => {
                        this.group = groupDto.group;
                        this.refreshGroup();
                        if (this._dateRangeService.initialized()) {
                            this.loadReconciliationResults(this._dateRangeService.endDate() as string);
                        }
                    });
                }
            });
        }
    }

    // intialize series from group
    private init() {
        if (this.group) {
            this.refreshGroup();
        } else if (this.item && this.item.configuration.groupGuid) {
            this._groupService.getByGuid(this.item.configuration.groupGuid)
                .then(groupDto => {
                    this.group = groupDto.group;
                    this.refreshGroup();
                });
        }
    }

    private loadReconciliationResults(endDate: string) {
        this.isLoading = true;
        return this._requestService.getAtV1<ReconciliationResults>(`reconciliationResults`,
            { date: endDate, guid: this.item.configuration.groupGuid })
            .then(data => this.results = data.results)
            .catch((reason) => console.error(reason))
            .finally(() => this.isLoading = false);
    }

    private refreshGroup() {
        this.isLoading = true;

        this._groupService.getIdentifiers(this.group.reconciliationMatrix.map(r => r.guid))
            .then(data => {
                const allGuids = data
                    .filter(d => d.type === IdentifierType.Tag)
                    .map(d => d.tagDefinitionGuid)
                    .concat(data.filter(d => d.type !== IdentifierType.Tag).map(d => d.guid));

                return this._identifierVmService.getViewModelsFromApiFromGuidList(allGuids);
            })
            .then(data => {
                this._viewModels = data;
            })
            .finally(() => {
                this.isLoading = false;
                return this.refresh();
            });
    }

    private refresh() {
        this.isLoading = true;
        const results = this.results || [];
        let res = _.sortBy(results, r => r.penalty);
        res = res.reverse();
        if (this._viewModels) {
            this.mergeKpi(res, this.kpiTuples);
        }
        this.isLoading = false;
    }

    private createEmptySeries(identifiers: IValueViewModel[]): SerieViewModel[] {
        const tags = identifiers.filter(i => i.identifierType === IdentifierType.Tag);
        return tags.map(t => this.createEmptySerie(t));
    }

    private createEmptySerie(item: IValueViewModel): SerieViewModel {
        const kpi: Kpi = {
            name: item.name,
            value: 0,
            min: 0,
            max: 0,
            globalMin: item.min,
            globalMax: item.max,
            target: 0,
            quality: '',
            color: infoTextColor,
            unit: item.unitName,
            variation: 0,
            context: '',
            identifier: item.guid
        };
        return {
            kpi: kpi,
            options: {
                isContextVisible: false,
                isTendencyVisible: false,
                isQualityVisible: true,
                isTargetVisible: true,
                isRangeVisible: true,
                decimals: 2,
                valueFormat: d3.format('2.2f')
            },
            item: item,
            penalty: 0
        };
    }

    private mergeKpi(results: ReconciliationResult[], series: SerieViewModel[]) {
        this.isLoading = true;
        const tuples: SerieViewModel[] = this._viewModels.map(d => {
            const serie = this.createEmptySerie(d);
            this.updateKpiSerieFromResult(serie, this.getResult(results, d));
            (serie as any).id = `reconciliationVariable${serie.kpi.identifier}${getRandomFloat().toString(36).slice(2)}`;
            return serie;
        });
        this.kpiTuples = tuples.sort((a, b) => b.penalty - a.penalty);
        this.kpiVmList = this.kpiTuples.map(d => {
            const result: any = d.kpi;
            result.options = d.options;
            return result;
        });
        this.isLoading = false;
    }

    private getResult(results: ReconciliationResult[], d: IValueViewModel)
        : ReconciliationResult {
        let result = _.find(results, r => r.guid === d.guid);
        if (!result) {
            result = {
                guid: d.guid,
                averageValue: null,
                min: d.min,
                max: d.max,
                initialValue: null,
                penalty: null,
                reconciledValue: null,
                resolution: null,
                stdDev: null,
                tagName: d.name
            };
        }
        return result;
    }

    private updateKpiSerieFromResult(serie: SerieViewModel, result: ReconciliationResult): void {

        const kpi: Kpi = {
            name: result.tagName,
            value: result.reconciledValue,
            min: result.averageValue - result.resolution,
            max: result.averageValue + result.resolution,
            globalMin: result.min,
            globalMax: result.max,
            target: result.averageValue,
            quality: `Penalty: ${d3.format('2.2f')(result.penalty)}`,
            color: result.penalty >= 3.84 ? errorTextColor : (result.penalty >= 1.96 ? warningTextColor : infoTextColor),
            unit: serie.item.unitName,
            variation: 0,
            context: '',
            identifier: result.guid
        };
        serie.kpi = kpi;
        serie.penalty = result.penalty;
    }
}

export const wtReconciliationMeasure: ComponentDefinition = {
    componentId: 'wtReconciliationMeasure',
    controller: ReconciliationMeasureController,
    template: require('./reconciliationMeasure.component.html'),
    controllerAs: 'vm',
    bindings: {
        group: '=?',
        item: '<?',
        ratio: '<?',
        results: '=?',
        selfRefresh: '@?'
    }
};

interface SerieViewModel {
    item: IValueViewModel;
    kpi: Kpi;
    options: any;
    penalty: number;
}